import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import {
  CONSOLE_CITY_ONBOARDING_CHECKLIST_DOC_URL,
  CONSOLE_CITY_ONBOARDING_SOP_DOC_URL,
  getCityFromSearchParams,
  getConsoleCityTabPath,
  getConsoleMeetingsRangeFromSearchParams,
} from "../../utils/consoleUtils";
import "../../styles/console.css";

const DOC_ACTION_BTN =
  "tb-btn inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm";

export default function OnboardingSopView() {
  const [searchParams] = useSearchParams();
  const cityId = getCityFromSearchParams(searchParams);
  const meetingsRange = getConsoleMeetingsRangeFromSearchParams(searchParams);
  const coveragePath = cityId ? getConsoleCityTabPath("coverage", cityId, meetingsRange) : null;
  const runsPath = cityId ? getConsoleCityTabPath("runs", cityId, meetingsRange) : null;

  return (
    <ConsoleViewShell loading={false} error={null}>
      <div className="console-main-inner">
        <ConsolePageHeader
          label="Playbook"
          title="City Onboarding SOP"
          sub="The standard for expansion is weekly utility: recurring runs and dependable coverage."
        />

        <div className="flex flex-wrap gap-2 mb-4">
          <a
            href={CONSOLE_CITY_ONBOARDING_SOP_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={DOC_ACTION_BTN}
          >
            Open full SOP
          </a>
          <a
            href={CONSOLE_CITY_ONBOARDING_CHECKLIST_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={DOC_ACTION_BTN}
          >
            Open checklist
          </a>
          {coveragePath && (
            <Link to={coveragePath} className={DOC_ACTION_BTN}>
              Open coverage
            </Link>
          )}
          {runsPath && (
            <Link to={runsPath} className={DOC_ACTION_BTN}>
              Open runs
            </Link>
          )}
        </div>

        <div className="focus-card mb-4">
          <div className="fc-eyebrow">Core Question</div>
          <div className="text-sm text-[var(--console-ink)]">
            Can a runner open RunCards in this city and reliably find a good run any day of the week, AM or PM?
          </div>
        </div>

        <div className="metrics-grid">
          <div className="mc" id="onboardingPhase1">
            <div className="mc-label">Phase 1</div>
            <div className="mc-val">Anchor club selection</div>
            <div className="mc-sub">Identify 4–6 defining clubs before run entry.</div>
          </div>
          <div className="mc" id="onboardingPhase2">
            <div className="mc-label">Phase 2</div>
            <div className="mc-val">Recurring run capture</div>
            <div className="mc-sub">Capture all valid recurring runs for anchor clubs.</div>
          </div>
          <div className="mc" id="onboardingPhase3">
            <div className="mc-label">Phase 3</div>
            <div className="mc-val">Coverage grid fill</div>
            <div className="mc-sub">Fill AM/PM gaps to maximize weekly utility.</div>
          </div>
          <div className="mc" id="onboardingPhase4">
            <div className="mc-label">Phase 4</div>
            <div className="mc-val">Completion check</div>
            <div className="mc-sub">Do not move on until Definition of Done is met.</div>
          </div>
        </div>

        <div className="focus-card mt-4">
          <div className="fc-eyebrow">Definition of Done</div>
          <div className="text-sm text-[var(--console-ink)]">
            4–6 anchor clubs, all anchor recurring runs entered, at least 8/14 cells covered, at least 50 recurring
            weekly runs, editorial-quality photos, and city-level trust for local and traveler use.
          </div>
        </div>

        <div className="focus-card mt-4" id="onboardingChecklistTracking">
          <div className="fc-eyebrow">Checklist Tracking</div>
          <div className="text-sm text-[var(--console-muted)]">
            Per-city checklist tracking is coming soon. For now, use the linked checklist doc and city tabs (Clubs,
            Runs, Coverage) to execute and verify progress.
          </div>
        </div>
      </div>
    </ConsoleViewShell>
  );
}
